/**
 * Styling for the two reduction landing pages (rate and payment).
 *
 * Written standalone rather than on top of `-/styles/sections/greeting`, which the
 * default page uses. That section renders `<b>` at 24px and NORMAL weight -- a quirk the
 * default copy is built around -- and these pages are typeset from the approved
 * comps, where the emphasis is genuinely bold and the hierarchy is carried by three
 * distinct sizes: the greeting, the headline figure, and the body.
 *
 * THREE ROLES, and which theme token fills each one is the whole design of this file.
 * The comps are Alliance's, but every creditor runs this app under its own palette and
 * two of them use the brand tokens INVERSELY:
 *
 *              primary            secondary
 *   Alliance   #222e43 navy       #00b8d2 light blue
 *   Evolve     #ba2234 red        #414042 charcoal
 *
 * So `primary` is not "the dark one" and `secondary` is not "the accent". Reading them
 * that way put Evolve's body copy in red and its headline figure in charcoal -- every
 * word emphasised, so none of it was.
 *
 *   MESSAGE  -> `colors.greeting` (#484848). Charcoal, and the only colour here that is
 *               NOT a brand token: the creditor payload sets primary/secondary/tertiary/
 *               quaternary/link/error/header and never `greeting`, so it is the same
 *               charcoal for everyone and cannot be turned into an accent by accident.
 *   BENEFIT  -> `colors.link`. The one token whose VALUE is the emphasis colour for both
 *               (#ba2234 for Evolve, #00b8d2 for Alliance). Not `secondary`, which is
 *               Evolve's charcoal and would leave the figure unemphasised; not `primary`,
 *               which is the button fill and would turn Alliance's figure navy.
 *   ACTION   -> `colors.primary`, via `mainButton`. Already correct: red for Evolve, navy
 *               for Alliance.
 *
 * Rules stay on `secondary` deliberately -- Alliance's light blue, as the comps show, and
 * a neutral charcoal divider for Evolve rather than yet more red.
 */
import {important, rem} from '-/styles/utils';

/** The comps are a ~660px column; below this the type has to step down. */
const narrow = content => ({
    '@media all and (max-width: 560px)': content
});

/*
 * EVERY BLOCK HERE ZEROES ITS TOP MARGIN.
 *
 * The app's reset does not cover `p`, so the UA's `margin-block-start: 1em` applies --
 * and 1em of a 68px figure is 68px of space above it. The comps are tightly set, so all
 * vertical rhythm on these pages is expressed as `marginBottom` alone and every block
 * starts from zero.
 */
const block = {
    marginTop: important(0),
    textAlign: 'center'
};

export default theme => {
    // See the three roles above. `secondary` is the fallback only for a creditor with no
    // link colour set; the schema gives every creditor one.
    const accent = theme.colors.link || theme.colors.secondary;
    const message = theme.colors.greeting;

    return {
        root: {
            marginBottom: rem(theme.greetingSpacing),
            padding: `${rem(4)} ${rem(16)} 0`,
            color: message,
            textAlign: 'center',

            '& h1': {
                ...block,
                marginBottom: important(rem(8)),
                color: message,
                fontSize: important(rem(26)),
                fontWeight: 'bold',
                lineHeight: important(rem(32)),
                ...narrow({
                    fontSize: important(rem(21)),
                    lineHeight: important(rem(26))
                })
            }
        },

        /** Body copy. Regular weight; the pages ask for bold explicitly where the comps do. */
        p: {
            ...block,
            marginBottom: important(rem(8)),
            color: message,
            fontSize: important(rem(18)),
            fontWeight: 'normal',
            lineHeight: important(rem(25)),
            ...narrow({
                fontSize: important(rem(16)),
                lineHeight: important(rem(22))
            })
        },

        /** The last line before the button needs more air than the lines above it. */
        beforeCta: {
            marginBottom: important(rem(20))
        },

        bold: {
            fontWeight: 'bold'
        },

        /*
         * "You locked down your 6.49% rate!*" -- the line under the greeting.
         *
         * TWO COLOURS, not one. The sentence is charcoal like the rest of the message and
         * only the figure takes the accent, so the number is what the eye lands on. A
         * whole line in the accent colour emphasises nothing, which is the same trap that
         * put Evolve's entire body in red.
         */
        headline: {
            ...block,
            marginBottom: important(rem(10)),
            color: message,
            fontSize: important(rem(30)),
            fontWeight: 'bold',
            lineHeight: important(rem(36)),
            ...narrow({
                fontSize: important(rem(23)),
                lineHeight: important(rem(28))
            })
        },
        /** The figure inside that line: the personalised benefit, in the accent. */
        headlineValue: {
            color: accent
        },

        /*
         * "$109/MONTH". One line, three sizes, sharing a baseline: the figure dominates, the
         * dollar sign sits a little under it and the period a lot under it, which is what
         * makes the number readable at a glance on a page the member skims.
         */
        amount: {
            ...block,
            marginBottom: important(rem(8)),
            color: accent,
            fontSize: important(rem(68)),
            fontWeight: 'bold',
            letterSpacing: -1,
            lineHeight: important(rem(70)),
            whiteSpace: 'nowrap',
            ...narrow({
                fontSize: important(rem(46)),
                lineHeight: important(rem(50))
            })
        },
        amountSymbol: {
            fontSize: '0.64em'
        },
        amountPeriod: {
            fontSize: '0.4em',
            letterSpacing: 0
        },

        /*
         * The comps use a rounded rectangle here, not the pill `mainButton` produces, and no
         * drop shadow. Overridden with `!important` rather than by ordering: Button carries
         * its own JSS sheet and there is no guarantee which of the two is inserted last.
         */
        button: {
            maxWidth: rem(430),
            marginBottom: important(rem(4)),
            padding: important(`${rem(15)} ${rem(20)}`),
            borderRadius: important(rem(8)),
            boxShadow: important('none'),
            fontSize: important(rem(16)),
            letterSpacing: important(1),
            ...narrow({
                fontSize: important(rem(14)),
                padding: important(`${rem(13)} ${rem(14)}`)
            })
        },
        /** The arrow trails the label with real space between them, as in the comps. */
        buttonArrow: {
            marginLeft: rem(10),
            fontWeight: 'normal'
        },

        rule: {
            width: '100%',
            maxWidth: rem(560),
            margin: `${rem(20)} auto`,
            border: 'none',
            borderTop: `1px solid ${theme.colors.secondary}`
        },

        reward: {
            ...block,
            margin: important(`0 auto ${rem(4)}`),
            maxWidth: rem(560),
            color: message,
            fontSize: important(rem(17)),
            fontWeight: 'normal',
            lineHeight: important(rem(24)),
            ...narrow({
                fontSize: important(rem(15)),
                lineHeight: important(rem(21))
            })
        },
        /*
         * "ADD UP TO $1,000 MORE TO YOUR REWARD." -- the bonus reward, on its own line.
         *
         * Set apart from the sentence above it because it is the part worth noticing, and
         * upper-cased in CSS rather than in the copy so the DOM keeps sentence case: a
         * screen reader reads a sentence, not an initialism, and the text stays greppable.
         */
        rewardAccentLine: {
            ...block,
            margin: important(`0 auto ${rem(12)}`),
            maxWidth: rem(560),
            color: accent,
            fontSize: important(rem(18)),
            fontWeight: 'bold',
            letterSpacing: 0.3,
            lineHeight: important(rem(25)),
            textTransform: 'uppercase',
            ...narrow({
                fontSize: important(rem(15)),
                lineHeight: important(rem(21))
            })
        },

        notMe: {
            ...block,
            marginBottom: important(0),
            fontSize: important(rem(15)),
            lineHeight: important(rem(21))
        },
            notMeLink: {
                color: important(message),
                textDecoration: 'underline'
            }
    };
};
